import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { compact } from 'lodash-es';
import { Badge, Box, DropdownMenu, DropdownMenuItem, IconButton, Page, PageContentHeader, PageErrorState, Tabs, Typography } from 'seagull';
import { Archive, Edit, Kebab, Trash, Unarchive } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import PageLoadingState from '~/components/PageLoadingState';
import { routes } from '~/constants';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useClientArchiveRestoreDelete } from '~/hooks/useClientArchiveRestoreDelete';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { eligibilityTooltipTitle, eligibilityTooltipsTextsPath } from '~/pages/ClientsPage/ClientsPage.utils';
import { formatShortMonthUSDate } from '~/utils/date-formatting';
import styles from './ClientDetailPage.module.scss';
import { CLIENT_DETAIL_TABS, DEFAULT_CLIENT_DETAIL_TAB } from './ClientDetailPage.types';
import { useBillClient } from './hooks/useBillClient';
import { useCustomerDetail } from './hooks/useCustomerDetail';
import { IssuedInvoicesTab } from './tabs/IssuedInvoicesTab/IssuedInvoicesTab';
import { dropWithdrawnAgreements } from './tabs/SummaryTab/cards/AgreementsOverviewCard/RecentAgreements.utils';
import { SummaryTab } from './tabs/SummaryTab/SummaryTab';
import { UpcomingInvoicesTab } from './tabs/UpcomingInvoicesTab/UpcomingInvoicesTab';
export const clientDetailPageTextsPath = 'pages.clientDetail';
export const GetClientDetailPageTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientDetailPage(param) {
    let { clientId } = param;
    const navigate = useNavigate();
    const { ClientDetailPageTestIds: TID } = useMemo(()=>GetClientDetailPageTID(), []);
    const { t: clientDetailTexts } = useLocaleKeys(clientDetailPageTextsPath);
    const { t: clientsDashboardTexts } = useLocaleKeys('pages.clientsDashboard');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const onBillClient = useBillClient(clientId);
    const openEditClient = useOpenEditClientModal();
    const { clientDetails, isLoading, isError, isNotFound, refresh } = useCustomerDetail(clientId, {
        refreshOnHookMount: true
    });
    const queryClient = useQueryClient();
    const onArchiveOrRestoreComplete = useCallback(()=>{
        refresh();
        queryClient.setQueryData([
            QueryKeys.RecentAgreements,
            clientId
        ], dropWithdrawnAgreements);
    }, [
        refresh,
        queryClient,
        clientId
    ]);
    const { onArchive, onRestore, onDelete } = useClientArchiveRestoreDelete({
        onComplete: onArchiveOrRestoreComplete,
        onDeleteComplete: ()=>navigate(routes.clients)
    });
    const isArchived = (clientDetails === null || clientDetails === void 0 ? void 0 : clientDetails.status) === 'archived';
    const { urlState, changeUrlState } = useStateFromUrl({
        tab: {
            defaultValue: DEFAULT_CLIENT_DETAIL_TAB
        }
    });
    const availableTabs = isArchived ? CLIENT_DETAIL_TABS.filter((tab)=>tab !== 'upcomingInvoices') : CLIENT_DETAIL_TABS;
    const currentTab = availableTabs.includes(urlState.tab) ? urlState.tab : DEFAULT_CLIENT_DETAIL_TAB;
    const onBack = ()=>navigate(routes.clients);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            testId: TID.Root(),
            onClickBack: onBack
        });
    }
    if (isError && clientDetails == null) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorState(),
            title: clientDetailTexts.loadError(),
            onRefresh: refresh
        });
    }
    if (isNotFound || clientDetails == null) {
        return /*#__PURE__*/ _jsx(Page, {
            testId: TID.Root(),
            header: clientDetailTexts.notFound(),
            onClickBack: onBack,
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.NotFound(),
                children: clientDetailTexts.notFound()
            })
        });
    }
    const statusBadgeSkin = clientDetails.status === 'active' ? 'success' : 'default';
    const isActive = clientDetails.status === 'active';
    const canArchive = clientDetails.archiveEligibility.type !== 'notEligible';
    const canDelete = clientDetails.deleteEligibility.type !== 'notEligible';
    const archiveTooltipTitle = eligibilityTooltipTitle(clientDetails.archiveEligibility, 'archive', eligibilityTooltipsTexts);
    const deleteTooltipTitle = eligibilityTooltipTitle(clientDetails.deleteEligibility, 'delete', eligibilityTooltipsTexts);
    const tabItems = availableTabs.map((tab)=>({
            id: tab,
            value: tab,
            component: /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                weight: "light",
                skin: "standard",
                children: clientDetailTexts.tabs[tab]()
            }),
            testId: TID.Tab(tab)
        }));
    return /*#__PURE__*/ _jsxs(Page, {
        testId: TID.Root(),
        className: styles.page,
        header: /*#__PURE__*/ _jsxs(Box, {
            className: styles.headerRow,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    variant: "h4",
                    className: styles.clientName,
                    testId: TID.ClientName(),
                    children: clientDetails.name
                }),
                /*#__PURE__*/ _jsx(Badge, {
                    skin: statusBadgeSkin,
                    size: "small",
                    rounded: true,
                    outlined: true,
                    testId: TID.StatusBadge(),
                    children: clientDetails.status ? clientDetailTexts.status[clientDetails.status]() : ''
                })
            ]
        }),
        subheader: /*#__PURE__*/ _jsxs(Box, {
            className: styles.subheader,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph3",
                    weight: "light",
                    testId: TID.ClientType(),
                    skin: "secondary",
                    children: clientDetails.clientType ? clientDetailTexts.header.type[clientDetails.clientType]() : ''
                }),
                clientDetails.createdOn > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            weight: "light",
                            skin: "secondary",
                            children: "•"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            weight: "light",
                            testId: TID.CreatedOn(),
                            skin: "secondary",
                            children: clientDetailTexts.header.createdOn({
                                date: formatShortMonthUSDate(new Date(clientDetails.createdOn))
                            })
                        })
                    ]
                })
            ]
        }),
        actions: /*#__PURE__*/ _jsx(DropdownMenu, {
            gap: 0,
            target: /*#__PURE__*/ _jsx(IconButton, {
                bi: {
                    name: 'client_clientDetails_menu'
                },
                testId: TID.Menu(),
                skin: "transparent",
                children: /*#__PURE__*/ _jsx(Kebab, {
                    size: 20
                })
            }),
            children: compact([
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_clientDetails_edit'
                    },
                    testId: TID.MenuEdit(),
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    onClick: ()=>openEditClient({
                            clientId: clientDetails.id,
                            initialName: clientDetails.name,
                            initialClientType: clientDetails.clientType
                        }),
                    children: clientDetailTexts.header.menu.editClient()
                }, "edit"),
                isActive && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_clientDetails_archive'
                    },
                    testId: TID.MenuArchive(),
                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                    onClick: ()=>onArchive(clientDetails),
                    disabled: !canArchive,
                    tooltipProps: archiveTooltipTitle ? {
                        title: archiveTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.archive()
                }, "archive"),
                isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_clientDetails_unarchive'
                    },
                    testId: TID.MenuRestore(),
                    iconStart: /*#__PURE__*/ _jsx(Unarchive, {}),
                    onClick: ()=>onRestore(clientDetails),
                    children: clientsDashboardTexts.actions.unarchive()
                }, "restore"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'client_clientDetails_delete'
                    },
                    testId: TID.MenuDelete(),
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    onClick: ()=>onDelete(clientDetails),
                    disabled: !canDelete,
                    skin: "error",
                    tooltipProps: deleteTooltipTitle ? {
                        title: deleteTooltipTitle,
                        placement: 'bottom'
                    } : undefined,
                    children: clientsDashboardTexts.actions.delete()
                }, "delete")
            ])
        }),
        mobile: {
            showActions: true
        },
        onClickBack: onBack,
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsx(Tabs, {
                    testId: TID.TabBar(),
                    bi: {
                        name: 'client_clientDetails_tabs'
                    },
                    value: currentTab,
                    onChange: (_event, value)=>changeUrlState({
                            tab: value
                        }),
                    tabs: tabItems
                })
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.tabContent,
                testId: TID.TabContent(),
                children: [
                    currentTab === 'summary' && /*#__PURE__*/ _jsx(SummaryTab, {
                        clientId: clientId,
                        clientName: clientDetails.name,
                        isArchived: isArchived,
                        onManageBilling: ()=>changeUrlState({
                                tab: 'upcomingInvoices'
                            }),
                        onViewUpcomingInvoice: ()=>changeUrlState({
                                tab: 'upcomingInvoices'
                            }),
                        onBillClient: onBillClient,
                        onViewAllOverdue: ()=>navigate("".concat(routes.client(clientId), "?tab=issuedInvoices&status=overdue"))
                    }),
                    currentTab === 'upcomingInvoices' && /*#__PURE__*/ _jsx(UpcomingInvoicesTab, {
                        clientId: clientId,
                        onBillClient: onBillClient
                    }),
                    currentTab === 'issuedInvoices' && /*#__PURE__*/ _jsx(IssuedInvoicesTab, {
                        clientId: clientId,
                        onBillClient: onBillClient,
                        isArchived: isArchived
                    })
                ]
            })
        ]
    });
}
